import { Tooltip } from 'antd';
import {
  SUMMARY_CHART_AREA_NOT_CHARTED_MESSAGE,
  SUMMARY_CHART_NO_DATA_MESSAGE,
  SUMMARY_DATA_BASIS_ACTUAL,
  SUMMARY_DATA_BASIS_LABELS,
  SUMMARY_DATA_BASIS_PLAN,
  SUMMARY_METRIC_COST_PER_HA,
  SUMMARY_METRIC_QUANTITY,
} from 'constants/uiConstants';
import ChartGhostState from 'pages/FarmsPage/Workspace/FarmDiagnostics/ChartGhostState';
import React, { useMemo } from 'react';
import { formatCurrency } from 'utils/CurrencyUtils';
import type { SummaryDataMetric } from '../../../../../redux/slices/InsightsSlice';
import { scaleForDisplay } from '../insightUtils';
import {
  CHART_GROUP_COST,
  CHARTABLE_SUMMARY_METRICS,
  computeInputSummaryChart,
  type InputSummaryChartBar,
  type InputSummaryChartGroup,
} from './computeInputSummaryChart';
import type { SummaryReportSideBySideRow } from './computeSummaryReport';
import type { SummaryDataSide } from './dataBasis';
import { formatNumber } from './formatNumber';

interface InputSummaryChartProps {
  rows: SummaryReportSideBySideRow[];
  sides: SummaryDataSide[];
  metric: SummaryDataMetric;
  /** The section's own $ labels — "Cost ($)" / "Avg. $/ha", or "Income ($)" / "Avg. Income/ha" for Production. */
  costLabel: string;
  costPerHaLabel: string;
}

/** Plan vs Actual uses the reserved chart-7 / chart-8 pair. With only one side there's nothing to compare, so bars are neutral grey — colour isn't carrying any meaning there. */
const PAIRED_SWATCH: Record<SummaryDataSide, string> = {
  [SUMMARY_DATA_BASIS_PLAN]: 'bg-chart-7',
  [SUMMARY_DATA_BASIS_ACTUAL]: 'bg-chart-8',
};
const SINGLE_SIDE_FILL = 'fill-gray-dark';
const SINGLE_SIDE_SWATCH = 'bg-gray-dark';

const QUANTITY_GROUP_LABELS: Record<string, string> = {
  kg: 'Mass',
  L: 'Liquid',
};

/** Axis heading and value formatter for one group — quantities scale kg → t using the group maximum, so every bar in a group shares one unit. */
const resolveGroupFormat = (
  group: InputSummaryChartGroup,
  metric: SummaryDataMetric,
  costLabel: string,
  costPerHaLabel: string
): { heading: string; format: (value: number) => string } => {
  if (group.key === CHART_GROUP_COST) {
    const perHa = metric === SUMMARY_METRIC_COST_PER_HA;
    return {
      heading: perHa ? costPerHaLabel : `Total ${costLabel.toLowerCase()}`,
      format: (value) => `${formatCurrency(value)}${perHa ? '/ha' : ''}`,
    };
  }
  const { unit } = scaleForDisplay(group.max, group.key || undefined);
  const divisor = unit === 't' ? 1000 : 1;
  const unitLabel = unit ?? '';
  const name = QUANTITY_GROUP_LABELS[group.key] ?? 'Quantity';
  return {
    heading: unitLabel ? `${name} (${unitLabel})` : name,
    format: (value) =>
      `${formatNumber(value / divisor)}${unitLabel ? ` ${unitLabel}` : ''}`,
  };
};

const pctOf = (value: number | undefined, max: number): number =>
  value && max > 0 ? Math.min(100, (value / max) * 100) : 0;

/**
 * One product's bar. With both sides, Plan is an outlined track and Actual a
 * solid bar inside it (the Live totals style); with one side it's a single
 * solid bar. Bar width is a continuous per-row value, so it's an SVG rect
 * width on a stretched 0–100 viewBox rather than a Tailwind class.
 */
const ChartBarRow: React.FC<{
  bar: InputSummaryChartBar;
  sides: SummaryDataSide[];
  max: number;
  format: (value: number) => string;
}> = ({ bar, sides, max, format }) => {
  const isPaired = sides.length > 1;
  const plan = bar.values[SUMMARY_DATA_BASIS_PLAN];
  const actual = bar.values[SUMMARY_DATA_BASIS_ACTUAL];
  const valueText = (side: SummaryDataSide) => {
    const value = bar.values[side];
    return value === undefined ? '—' : format(value);
  };

  return (
    <Tooltip
      title={sides
        .map((side) => `${SUMMARY_DATA_BASIS_LABELS[side]}: ${valueText(side)}`)
        .join(' · ')}
    >
      <div className="flex cursor-help items-center gap-3">
        <span
          className="w-40 shrink-0 truncate text-sm text-charcoal"
          title={bar.label}
        >
          {bar.label}
        </span>
        <svg
          viewBox="0 0 100 10"
          preserveAspectRatio="none"
          className="h-4 min-w-0 flex-1"
          aria-hidden="true"
        >
          {isPaired ? (
            <>
              {plan !== undefined && (
                <rect
                  x={0}
                  y={0.5}
                  width={pctOf(plan, max)}
                  height={9}
                  fillOpacity={0.2}
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                  className="fill-chart-7 stroke-chart-7"
                />
              )}
              {actual !== undefined && (
                <rect
                  x={0}
                  y={2.5}
                  width={pctOf(actual, max)}
                  height={5}
                  className="fill-chart-8"
                />
              )}
            </>
          ) : (
            <rect
              x={0}
              y={1}
              width={pctOf(bar.values[sides[0]], max)}
              height={8}
              className={SINGLE_SIDE_FILL}
            />
          )}
        </svg>
        <span className="flex w-36 shrink-0 flex-col text-right text-xs tabular-nums text-gray-dark">
          {sides.map((side) => (
            <span key={side}>
              {isPaired && `${SUMMARY_DATA_BASIS_LABELS[side]} `}
              <span className="font-semibold text-charcoal">
                {valueText(side)}
              </span>
            </span>
          ))}
        </span>
      </div>
    </Tooltip>
  );
};

/** Placeholder bars rendered (dimmed) under ChartGhostState, so an empty chart keeps its shape. */
const GHOST_BAR_WIDTHS = [85, 60, 40];

const GhostBars: React.FC = () => (
  <div className="flex flex-col gap-2">
    {GHOST_BAR_WIDTHS.map((width) => (
      <div key={width} className="flex items-center gap-3">
        <span className="h-3 w-40 shrink-0 rounded-sm bg-gray-smoke" />
        <svg
          viewBox="0 0 100 10"
          preserveAspectRatio="none"
          className="h-4 min-w-0 flex-1"
          aria-hidden="true"
        >
          <rect x={0} y={1} width={width} height={8} className="fill-gray-medium" />
        </svg>
        <span className="h-3 w-36 shrink-0 rounded-sm bg-gray-smoke" />
      </div>
    ))}
  </div>
);

/**
 * Input summaries chart (OP-2394) — horizontal bars per product, largest
 * first, driven by the page-level data basis and the metric picker. Quantity
 * splits into one axis per unit (mass / liquid), as Quantity required does.
 */
const InputSummaryChart: React.FC<InputSummaryChartProps> = ({
  rows,
  sides,
  metric,
  costLabel,
  costPerHaLabel,
}) => {
  const groups = useMemo(
    () => computeInputSummaryChart(rows, sides, metric),
    [rows, sides, metric]
  );

  const isEmpty = groups.length === 0;
  const ghostMessage = CHARTABLE_SUMMARY_METRICS.has(metric)
    ? SUMMARY_CHART_NO_DATA_MESSAGE
    : SUMMARY_CHART_AREA_NOT_CHARTED_MESSAGE;

  return (
    <div className="w-full rounded-md border border-gray-bright p-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {sides.map((side) => (
          <span
            key={side}
            className="inline-flex items-center gap-1.5 text-sm text-gray-dark"
          >
            <span
              className={`inline-block h-2.5 w-2.5 rounded-sm ${sides.length > 1 ? PAIRED_SWATCH[side] : SINGLE_SIDE_SWATCH}`}
            />
            {SUMMARY_DATA_BASIS_LABELS[side]}
          </span>
        ))}
        {metric === SUMMARY_METRIC_QUANTITY && groups.length > 1 && (
          <span className="text-xs text-gray-muted">
            Mass and liquid are charted on separate scales.
          </span>
        )}
      </div>
      <div className="mt-3">
        <ChartGhostState active={isEmpty} message={ghostMessage}>
          {isEmpty ? (
            <GhostBars />
          ) : (
            <div className="flex flex-col gap-5">
              {groups.map((group) => {
                const { heading, format } = resolveGroupFormat(
                  group,
                  metric,
                  costLabel,
                  costPerHaLabel
                );
                return (
                  <div key={group.key} className="flex flex-col gap-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-xs font-semibold text-gray-dark">
                        {heading}
                      </span>
                      <span className="text-xs text-gray-muted">
                        Max {format(group.max)}
                      </span>
                    </div>
                    {group.bars.map((bar) => (
                      <ChartBarRow
                        key={bar.key}
                        bar={bar}
                        sides={sides}
                        max={group.max}
                        format={format}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </ChartGhostState>
      </div>
    </div>
  );
};

export default InputSummaryChart;
